import useFetchClientAccountList from '@/hooks/useApi/useFetchClientAccountList';
import { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { CrossborderMessages } from 'vopay';
import { useClientData } from '@/providers/ClientData.provider';
import useGenerateGCMEmbedUrl from '@/hooks/useApi/useGenerateGCMEmbedUrl';
import { useBranding } from '@/providers/Branding.provider';
import { useBlockingLoader } from 'shared';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';

const GlobalSend: React.FC = () => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const blockingLoader = useBlockingLoader();
  const { primaryColor } = useBranding();
  const { userSettings } = useUserSettingsProvider();

  const {
    response: { clientAccountsEnabled },
  } = useClientData();

  const history = useHistory();

  const { path } = useParams<{
    path: string;
  }>();

  const [currentPath, setCurrentPath] = useState<string>();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [, , , fetchClientAccounts] = useFetchClientAccountList({});
  const { link, generateGCMUrl } = useGenerateGCMEmbedUrl();

  const postMessageToIframe = (message: CrossborderMessages) => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow?.postMessage(message, '*');
    }
  };

  useEffect(() => {
    if (currentPath === path || !isInitialized) return;

    postMessageToIframe({
      type: 'ACTION',
      code: 'NAVIGATION',
      payload: {
        path,
      },
    });

    setCurrentPath(path);
  }, [path, isInitialized]);

  useEffect(() => {
    // The loader is held until Global Send reports CONFIG_PROCESSED. If this effect is torn down first
    // (leaving the page early, or StrictMode's dev double-mount), the cleanup releases it.
    let isHoldingLoader = true;

    const releaseLoader = () => {
      if (!isHoldingLoader) return;
      isHoldingLoader = false;
      blockingLoader.hide();
    };

    const handleMessage = (event: MessageEvent<CrossborderMessages>) => {
      // if (event.origin !== 'http://localhost:4174') return; should do add later
      if (!event.data) return;
      const { type, code } = event.data;

      if (type === 'ACTION' && code === 'NAVIGATION') {
        const { path: newPath } = event.data.payload;
        setCurrentPath(newPath);
        // Replace, not push: Global Send already pushed its own entry for this move; a push here would leave two per move.
        history.replace(`/global-send/${newPath}`);
      }

      if (type === 'INFO' && code === 'INIT') {
        postMessageToIframe({
          type: 'ACTION',
          code: 'CONFIG',
          payload: {
            hideMenu: true,
            primaryColor: primaryColor,
            timezone: userSettings.timezone,
          },
        });

        setIsInitialized(true);
      }

      if (type === 'INFO' && code === 'CONFIG_PROCESSED') {
        releaseLoader();
        setIsLoaded(true);
      }
    };

    blockingLoader.show();
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      releaseLoader();
    };
  }, []);

  useEffect(() => {
    const fetchAccounts = async () => {
      if (clientAccountsEnabled) {
        const clientAccountsRes = await fetchClientAccounts({
          ClientType: '',
        });

        if (!clientAccountsRes.Success) throw new Error(clientAccountsRes.ErrorMessage);

        const primaryClientAccount = Object.values(clientAccountsRes.ClientAccounts || {}).find(
          (obj) => obj.IsPrimaryAccount === '1',
        );

        if (!primaryClientAccount) return;
        const { ClientAccountID } = primaryClientAccount;

        generateGCMUrl({
          ClientAccountID: ClientAccountID,
        });
      } else {
        generateGCMUrl({});
      }
    };

    fetchAccounts();
  }, []);

  return (
    <>
      {link && (
        <iframe
          ref={iframeRef}
          src={link}
          title="Global Send Iframe"
          style={{ width: '100%', height: '100%', border: 'none', visibility: isLoaded ? 'visible' : 'hidden' }}
          allow="clipboard-write"
          seamless
        ></iframe>
      )}
    </>
  );
};

export default GlobalSend;
